import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BrdocsCaptureResult } from '../docpic/docpic';
import { QrCaptureResult } from '../qr-capture/qr-capture';
import { Scanner } from '../scan/scanner';
import { canvasToPixels } from '../scan/browser-render';
import { extractFields, DocField, DocSignature, ParsedDocument } from '../../lib/brdocs';
import { documentTypeLabel } from '../../lib/brdocs/classify/document-label';
import { CaptureDebugEvent } from '../shared/debug-event';
import { TranslateService } from '@ngx-translate/core';
import { DocMeta, DOC_META, OTHER_FAMILY, metaFor, docFamily, isOpen, isDigital } from './doc-meta';
import { FileMetadata } from '../docpic/kyc-file-metadata.types';
import { CameraStreamService, CameraStartInfo } from '../docpic/camera-stream.service';
import { CaptureWatchdogService } from '../core/services/capture-watchdog.service';
import { Subscription } from 'rxjs';

/** The screens of the guided wizard, in order. */
type Step =
  | 'inicio'
  | 'escolha'
  | 'frente'
  | 'confere-frente'
  | 'tipo-doc'
  | 'verso-foto'
  | 'confere-verso'
  | 'interna-foto'
  | 'qr'
  | 'pdf-erro'
  | 'nao-documento'
  | 'processando'
  | 'resultado';

/** One label/value pair shown in the final result, tagged by its source. */
interface ResultField {
  label: string;
  value: string;
  /** 'qr' = read from the chip/QR (trustworthy); 'ocr' = read from the text (may err). */
  fonte: 'qr' | 'ocr';
}

/** Every user action the flow reports through the `(action)` output. */
export type CaptureFlowAction =
  | 'started' // tapped "Começar"
  | 'method-selected' // chose camera or device upload
  | 'front-captured' // front photo taken / file picked & identified
  | 'front-accepted' // confirmed the front looks good
  | 'front-retaken' // chose to redo the front
  | 'back-captured' // back photo taken
  | 'back-accepted' // confirmed the back looks good
  | 'back-retaken' // chose to redo the back
  | 'inner-captured' // photo of the opened document's inner (QR) page taken/picked
  | 'qr-read' // a QR/MRZ was decoded (live or from the upload)
  | 'qr-skipped' // gave up reading the QR
  | 'digital-export-accepted' // a genuine CNH-e export (signed PDF) was accepted
  | 'digital-picture-accepted' // a PICTURE of a CNH-e was accepted on its QR data (→ postpone)
  | 'digital-picture-blocked' // a picture of a CNH-e with no readable QR — user must export the PDF
  | 'not-document' // the is_document check found no document in the capture → retry screen
  | 'not-document-limit' // Nth "not a document" in a row → flow restarted from the welcome screen
  | 'not-document-overridden' // Nth "not a document" in a row, but the host let it through
  | 'not-document-blocked' // too many "not a document" overall → blocked for a while, flow restarted
  | 'voice-toggled' // turned spoken guidance on/off
  | 'navigated-back' // tapped the top-bar back button to the previous screen
  | 'completed' // the result screen was shown
  | 'restarted'; // tapped "capture another document"

/** A single user-action event emitted by the flow (for analytics / host UI). */
export interface CaptureFlowEvent {
  /** What the user just did. */
  action: CaptureFlowAction;
  /** The screen the flow is on as this fires. */
  step: string;
  /** The active input method, once chosen. */
  method: 'camera' | 'upload';
  /** Action-specific extra context (e.g. the document type, voice on/off). */
  detail?: Record<string, unknown>;
}

/** One extracted field in the {@link CaptureFlowResult}. */
export interface CaptureField {
  label: string;
  value: string;
  /** `'qr'` = read from the signed QR/MRZ (trustworthy); `'ocr'` = read from text (may err). */
  source: 'qr' | 'ocr';
}

/**
 * The compiled result emitted by `(completed)` once a document has been read.
 * Everything a host app needs: the type, the extracted fields, the holder photo,
 * the digital-signature status, and the captured images.
 */
export interface CaptureFlowResult {
  /** Stable type key — the classifier type (e.g. `'cnh-front'`) or the QR doc kind (`'cin'`). */
  type: string;
  /** Friendly document name (pt-BR), e.g. "CNH-e", "Identidade (CIN)". */
  documentName: string;
  /** False when the type couldn't be recognized. */
  recognized: boolean;
  /** Extracted fields, QR-sourced first (trustworthy) then OCR-sourced. */
  fields: CaptureField[];
  /** Holder photo as a data URL, when the signed QR/MRZ carried one. */
  photo: string | null;
  /** Digital-signature status from the signed QR/MRZ, when present. */
  signature: DocSignature | null;
  /** True when the data came from a decoded QR/MRZ (authoritative). */
  fromQrCode: boolean;
  /**
   * The decoded QR document kind when a QR was read (`'cnh'`, `'cin'`, `'mrz'`, or `'text'` for a
   * generic/unrecognised QR claimed by the fallback parser). Null when no QR decoded. Lets a host
   * distinguish a real identity QR from a junk front-side QR that only decoded to a URL/text.
   */
  qrKind?: string | null;
  /**
   * For a decoded CNH QR, whether reader-cnh confirmed the QR version's byte
   * layout against a real sample. `false` means an unverified version (1/3/4)
   * whose fields should be routed to manual review; `null`/`undefined` when no
   * versioned QR was read. See the vendored reader-cnh PROVENANCE.
   */
  versionVerified?: boolean | null;
  /** Captured/rendered images as data URLs (JPEG/PNG). */
  frontImage: string | null;
  backImage: string | null;
  /** A non-fatal note shown to the user (e.g. the QR couldn't be read). */
  warning: string | null;
  /**
   * How the document was provided: live `camera` vs a `upload` from the device. Additive diagnostic
   * (optional) so a host can log the capture method without subscribing to the action stream.
   */
  method?: 'camera' | 'upload';
  /** Raw bytes to persist per side (may differ from the display data URL). */
  frontRaw?: Blob | null;
  frontRawName?: string | null;
  frontMeta?: FileMetadata | null;
  backRaw?: Blob | null;
  backRawName?: string | null;
  backMeta?: FileMetadata | null;
  /**
   * True when this capture is a **picture of a digital CNH** (photographed screen or
   * printout) that we accepted on its signed QR data. Veriff cannot verify such an
   * image and will reject it; the host passes this to the provider so the rejection
   * becomes a terminal postpone (manual review, with authoritative QR data attached)
   * instead of bouncing the user into a resubmission loop they cannot satisfy.
   */
  digitalPicture?: boolean;
}

/**
 * What a host's is_document check decided for one capture (CPK-5103):
 *  - `document`       → continue to the "ficou boa?" preview, exactly as without a check;
 *  - `not_document`   → the "Não encontramos um documento nesta imagem" screen, retry that side;
 *  - `limit_restart`  → too many in a row: restart the flow from the welcome screen;
 *  - `limit_proceed`  → too many in a row, but let it through (same as `document`);
 *  - `blocked`        → too many overall: no new image for a while — restart the flow, the host
 *                       says until when.
 */
export type DocumentCheckOutcome = 'document' | 'not_document' | 'limit_restart' | 'limit_proceed' | 'blocked';

/** One capture handed to the host's is_document check, before its preview is shown. */
export interface DocumentCheckInput {
  /** The capture as the preview would show it (JPEG data URL; a PDF's rendered first page). */
  image: string;
  side: 'front' | 'back';
  method: 'camera' | 'upload';
  /** `pdf` when the image is the first page of an uploaded PDF. */
  source: 'image' | 'pdf';
}

/**
 * The host's is_document check. Must not throw for a failed check — fail open by resolving
 * `document` (the flow also treats a rejection as `document`, as a last line of defence).
 */
export type DocumentCheckFn = (input: DocumentCheckInput) => Promise<DocumentCheckOutcome>;

/** Friendly pt-BR labels for the OCR-extracted field keys. */
const OCR_LABELS: Record<string, string> = {
  nome: 'Nome',
  cpf: 'CPF',
  rg: 'RG',
  registroGeral: 'Registro Geral',
  dataNascimento: 'Data de nascimento',
  dataValidade: 'Validade',
  dataEmissao: 'Data de emissão',
  naturalidade: 'Naturalidade',
  orgaoExpedidor: 'Órgão expedidor',
  filiacao: 'Filiação',
  mae: 'Mãe',
  pai: 'Pai',
};

/**
 * Guided, low-literacy-friendly full capture of a Brazilian document.
 *
 * Flow: capture the **front** → identify the type → if that document has a
 * **back**, ask for it and capture it → if it carries a **QR code**, read and
 * verify it → show one **compiled result** with the photo, every field we could
 * extract, and the signature status.
 *
 * Everything is in Brazilian Portuguese, with big buttons, large icons, colour
 * and (optionally) **spoken** instructions at every step — built so someone with
 * little reading ability can complete it on a low-end phone.
 */
@Component({
  selector: 'app-full-capture, brdocs-capture-flow',
  templateUrl: './full-capture.html',
  styleUrls: ['./full-capture.scss'],
  providers: [CameraStreamService],
})
export class FullCapture implements OnInit, OnDestroy {
  constructor(
    private scanner: Scanner,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private camera: CameraStreamService,
    private watchdog: CaptureWatchdogService,
  ) {}

  // --- Public API (inputs / outputs) ---------------------------------------

  /** Start with spoken guidance on (default) or off. */
  @Input() voice = true;

  /**
   * How long (ms) the camera may auto-scan without a capture before the manual "Tirar foto"
   * escape hatch appears, forwarded to every `<brdocs-capture>`. Undefined leaves each capture
   * on its own default; the guided KYC host sets it per-action (from `documentScanTimeSeconds`).
   */
  @Input() manualFallbackMs?: number;

  /**
   * Embedded mode: the host app takes over once the document is read, so the
   * built-in **result screen is suppressed** (no extracted-data dump shown to the
   * user). `completed` still fires; the host shows whatever comes next. Used by
   * the KYC modal, where a confirmation step handles the data instead.
   */
  @Input() embedded = false;

  /**
   * Optional is_document check (CPK-5103), run on every front/back capture BEFORE its "ficou
   * boa?" preview. Unset (the default, and the testbench) → no check, the flow is unchanged.
   * Captures already proven by a signed QR (a CNH-e PDF, a picture of a CNH-e, a card whose QR
   * decoded) skip it, and so does the inner QR page (it is never previewed nor submitted).
   */
  @Input() documentCheck?: DocumentCheckFn | null;

  /**
   * Emitted once a document has been read and the result screen is shown — the
   * host app's hook to receive the extracted data ({@link CaptureFlowResult}).
   */
  @Output() completed = new EventEmitter<CaptureFlowResult>();

  /**
   * Emitted on **every user action** through the flow (started, method chosen,
   * captured, accepted, retaken, QR read/skipped, voice toggled, completed,
   * restarted) — a single stream for analytics or host-side reactions.
   */
  @Output() action = new EventEmitter<CaptureFlowEvent>();

  /**
   * Low-level **diagnostic** stream — camera lifecycle, OCR legibility,
   * classification scores, QR/signature reads, model loading, and errors,
   * forwarded from the camera/QR sub-components plus this flow's own steps. For
   * developer logging/troubleshooting, not end-user UI.
   */
  @Output() debug = new EventEmitter<CaptureDebugEvent>();

  /**
   * Emitted once per flow when the camera stream is first acquired, carrying the
   * device's camera settings + capabilities. The KYC host turns it into the
   * `KYC_CAMERA_STREAM_STARTED` analytics event (it owns the claim/case id).
   */
  @Output() cameraStarted = new EventEmitter<CameraStartInfo>();

  private cameraStartedSub?: Subscription;

  /** Re-emit a diagnostic from a child component on this flow's debug stream. */
  protected forwardDebug(e: CaptureDebugEvent): void {
    this.debug.emit(e);
  }

  /** Emit one diagnostic event from this flow's own orchestration. */
  private dbg(
    level: CaptureDebugEvent['level'],
    category: string,
    message: string,
    data?: Record<string, unknown>,
  ): void {
    this.debug.emit({ level, category, message, data });
  }

  // --- Internal state -------------------------------------------------------

  /** Current screen. */
  protected step: Step = 'inicio';
  /** How the document is provided: live camera, or a file from the device. */
  protected metodo: 'camera' | 'upload' = 'camera';
  /**
   * Set when an uploaded **digital** document was identified from its signed
   * QR/MRZ (no CNN, no review). Drives the result header name.
   */
  protected docDigital: ParsedDocument | null = null;
  /** Confirmed front / back captures from the embedded camera. */
  protected frente: BrdocsCaptureResult | null = null;
  protected verso: BrdocsCaptureResult | null = null;

  /**
   * True when the classifier says the captured "front" is actually a **back**
   * (its type ends with `-back`). Drives the wrong-side hint on `confere-frente`
   * so the user is nudged to show the front (photo side) first instead of
   * proceeding with the two sides swapped.
   */
  protected frentePareceVerso = false;

  /** The capture the is_document check refused, shown on the `nao-documento` screen. */
  protected naoDocumentoImg: string | null = null;
  /** Which side was refused — where "try again" and the back button return to. */
  protected naoDocumentoLado: 'frente' | 'verso' = 'frente';
  /**
   * Monotonic id of the in-flight is_document check: a late answer for a check the user has
   * since moved past (restart, flow torn down) must not change the screen.
   */
  private checagemId = 0;
  private destruido = false;

  /**
   * The document families offered by the manual type picker (shown when the
   * classifier couldn't recognise the document). The friendly names/icons come
   * straight from {@link DOC_META}; the "Other / not listed" fallback is offered
   * separately in the template.
   *
   * `pescador` (Carteira de Pescador) is intentionally hidden from this manual
   * picker — it is no longer offered as a manual pick (a user falls back to
   * "Other" instead). It stays in {@link DOC_META} so the classifier/extract
   * path still handles an auto-detected Carteira de Pescador.
   */
  protected readonly tiposManuais: { familia: string; nome: string; icon: string }[] =
    Object.entries(DOC_META)
      .filter(([familia]) => familia !== OTHER_FAMILY && familia !== 'pescador')
      .map(([familia, m]) => ({ familia, nome: m.nome, icon: m.icon }));

  /** Family key for the manual "Other / not listed" pick (template binding). */
  protected readonly outroFamilia = OTHER_FAMILY;

  /**
   * While true, the manual type picker shows the "Other / not listed" disclaimer
   * (we may need to contact the user to request another ID) with a single
   * "proceed anyway" action, instead of the family list. Reset whenever the
   * picker is (re)entered so it always opens on the list.
   */
  protected confirmandoOutro = false;

  /** Whether to speak the instructions aloud (on by default — for non-readers). */
  protected voz = true;

  /** Compiled output, set once everything has been read. */
  protected campos: ResultField[] = [];
  protected fotoPessoa: string | null = null;
  protected assinatura: DocSignature | null = null;
  protected leuQr = false;
  /** Kind of the decoded QR document (`'cnh'`/`'cin'`/`'mrz'`/`'text'`), null until a QR is read. */
  protected qrKind: string | null = null;
  /** reader-cnh version-verification of a decoded CNH QR; null until a versioned QR is read. */
  protected qrVersionVerified: boolean | null = null;
  /** A non-fatal note when something couldn't be read (e.g. QR not found). */
  protected aviso: string | null = null;

  /**
   * Which surface the QR step is aimed at: the outer `verso` (CIN), the `interna`
   * inner page of a folded document (CNH), or the `frente` (single-sheet certidão).
   */
  protected qrSide: 'frente' | 'verso' | 'interna' = 'verso';
  /**
   * Photo of the folded document's INNER page (the CNH's QR/MRZ surface), captured
   * on the upload path or when the live camera can't open. Read for its QR only —
   * deliberately absent from {@link buildResult}, because it is not the document's
   * back and Veriff rejects it as one (609 "Document back missing").
   */
  protected interna: BrdocsCaptureResult | null = null;

  /**
   * This capture is a picture of a digital CNH, accepted on its signed QR data.
   * Surfaces on {@link CaptureFlowResult.digitalPicture} so the provider can postpone
   * the check Veriff is certain to reject, rather than asking the user for a better
   * photo of something that will never verify.
   */
  protected fotoDeCnhDigital = false;

  /**
   * Back-of-document image for the result: the photo of the OUTER back, and nothing
   * else. This getter used to fall back to the still grabbed during the QR read —
   * which, for a CNH, is the inner page. That still travelled all the way to Veriff
   * as `document-back`, so every folded-CNH capture was declined with 609
   * "Document back missing" while the front verified fine. The back is now whatever
   * the user photographed at the `verso-foto` step, or nothing at all.
   */
  protected get versoImg(): string | null {
    return this.verso?.rawDataUrl ?? null;
  }

  /**
   * Illustration `kind` for the focused QR step. The CNH's QR is on an inner page —
   * the `cnh-abrir` hint loops "open + rotate"; every other QR-bearing doc (CIN
   * back, certidão front) reuses the flat `qr` hint.
   * Typed `string` and bound through `$any(...)` in the template because the
   * `cnh-abrir`/`verso-amostra` illustration kinds are added by the doc-illustration
   * component separately.
   */
  protected get ilustracaoQr(): string {
    return metaFor(this.frente?.type ?? '').qr === 'inner' ? 'cnh-abrir' : 'qr';
  }

  /**
   * Illustration `kind` for the back step. A folded document (CNH) gets the
   * categories-side sample, so the user can't confuse the outer back with the inner
   * QR page; everything else keeps the generic back sample.
   */
  protected get ilustracaoVerso(): string {
    return metaFor(this.frente?.type ?? '').qr === 'inner' ? 'cnh-verso' : 'verso-amostra';
  }

  /**
   * The "não estou com o documento agora" escape on the QR step. Hidden for the
   * first {@link ATRASO_PULAR_MS} so skipping isn't the path of least resistance —
   * the user gets a real chance to scan before being offered a way out.
   */
  protected podePular = false;
  private pularTimer?: ReturnType<typeof setTimeout>;

  /** Metadata for whatever the front was identified as. */
  protected get meta(): DocMeta | null {
    const f = this.frente;
    return f && f.recognized ? metaFor(f.type) : null;
  }

  /** Friendly document name for the header. */
  protected get nomeDoc(): string {
    const dig = this.docDigital;
    if (dig) return dig.title; // precise title from the signed payload (e.g. CNH-e)
    const m = this.meta;
    if (m) return m.nome;
    const f = this.frente;
    return f ? documentTypeLabel(f.type) : 'Documento';
  }

  /** Reused <audio> element for the spoken guidance clips. */
  private audio?: HTMLAudioElement;

  ngOnInit(): void {
    // Adopt the host's initial voice preference, then speak the opening screen.
    this.voz = this.voice;
    this.speakCurrent();
    this.cameraStartedSub = this.camera.started.subscribe((info) => this.cameraStarted.emit(info));
    // Watch for a frozen main thread during capture (see CaptureWatchdogService) —
    // the "infinite loading after the back upload" symptom logs a DB stall event.
    void this.watchdog.start({ provider: 'somoskyc_v1', embedded: this.embedded });
  }

  /**
   * Speak the guidance for the current screen (best-effort). Call right after any
   * change to `step` or `metodo` so the words match what's on screen.
   */
  private speakCurrent(): void {
    if (this.voz) {
      const { id, fala } = this.audioInfo();
      this.tocar(id, fala);
    }
  }

  /** trackBy for the result fields *ngFor. */
  trackByLabel(_i: number, c: { label: string }): string {
    return c.label;
  }

  // --- Navigation -----------------------------------------------------------

  /** Emit one user-action event (current step + method + optional detail). */
  private emitAction(action: CaptureFlowAction, detail?: Record<string, unknown>): void {
    this.action.emit({ action, step: this.step, method: this.metodo, detail });
  }

  /** Begin: ask how the document will be provided (camera or device file). */
  comecar(): void {
    this.reset();
    this.step = 'escolha';
    this.speakCurrent();
    this.emitAction('started');
    try { this.cdr.detectChanges(); } catch {}
  }

  /** User chose how to provide the document → go to the front step. */
  escolher(metodo: 'camera' | 'upload'): void {
    this.metodo = metodo;
    this.step = 'frente';
    this.speakCurrent();
    this.emitAction('method-selected', { method: metodo });
    try { this.cdr.detectChanges(); } catch {}
  }

  /** The embedded camera or the upload confirmed the front image (no signed QR read). */
  onFrenteConfirmed(r: BrdocsCaptureResult): void {
    // A picture of a digital CNH whose QR we could NOT read is the one thing we must
    // stop: there is no back to photograph, the image will never verify with Veriff, and
    // without the QR we hold no authoritative data either — so there is nothing to
    // postpone on. The remedy is the real PDF export, and only the user can fetch it.
    // (Had the QR decoded, `onDigital` would have accepted it and postponed instead.)
    // Anything else — an RG scan, a photo of the physical card, a certidão — is NEVER
    // blocked here; it falls through to the normal flow.
    if (this.ehFotoDeCnhDigital(r)) {
      this.dbg('warn', 'digital', 'picture of a digital CNH with no readable QR — asking for the PDF export', {
        type: r.type,
        confidence: r.confidence,
      });
      this.emitAction('digital-picture-blocked', { type: r.type, confidence: r.confidence });
      this.frente = null;
      this.docDigital = null;
      this.step = 'pdf-erro';
      this.speakCurrent();
      try { this.cdr.detectChanges(); } catch {}
      return;
    }
    if (this.documentCheck) {
      void this.checarDocumento(r, 'frente');
      return;
    }
    this.mostrarFrente(r);
  }

  /** Bank the front capture and show its "ficou boa?" preview. */
  private mostrarFrente(r: BrdocsCaptureResult): void {
    this.frente = r;
    // The classifier can tell a back was shot as the front (it emits `-back`
    // suffixed keys). Flag it so the review screen nudges the user to show the
    // front first, instead of silently proceeding with the sides swapped.
    this.frentePareceVerso = r.recognized && r.type.endsWith('-back');
    this.step = 'confere-frente';
    this.speakCurrent();
    this.emitAction('front-captured', {
      type: r.type,
      recognized: r.recognized,
      looksLikeBack: this.frentePareceVerso,
    });
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Is this image a picture/print of a **digital CNH** that carries no readable QR?
   *
   * Requires the classifier to be *confident* (`recognized`, and not a low-confidence
   * guess): a blurry photo of the physical card must never be mistaken for a screen and
   * blocked — that user has a perfectly verifiable document in their hand. And a QR that
   * already decoded means `onDigital` owns the decision, not this.
   */
  private ehFotoDeCnhDigital(r: BrdocsCaptureResult): boolean {
    return (
      !this.leuQr &&
      !this.docDigital &&
      r.recognized &&
      r.confidence !== 'low' &&
      docFamily(r.type) === 'cnh' &&
      isDigital(r.type)
    );
  }

  /**
   * An uploaded file yielded a **signed QR/MRZ**. Route it by PROVENANCE, not by the
   * signature — see docs/superpowers/specs/2026-07-12-cnh-e-provenance-routing-design.md.
   *
   * A SERPRO-signed QR proves the *data* is authentic; it says nothing about what the
   * *file* is. The physical CNH's inner page and the CNH-e PDF carry the same signed
   * payload, so a photo of the card's QR page verifies exactly like a genuine export.
   * Treating "signature verified" as "digital document" is what submitted a photo of a
   * QR page to Veriff as a lone front and earned 647 "Document not recognised".
   *
   * What actually separates them is the container and, for images, the CNN:
   *
   *  - **PDF + signed QR** → a genuine CNH-e export. It IS the whole document: proceed,
   *    no back step, no QR step.
   *  - **Image + `cnh-digital`** → a picture of a CNH-e. Veriff will reject it, but the
   *    QR gives us authoritative data, so don't block the user: proceed, and flag the
   *    check so the provider turns Veriff's rejection into a terminal postpone
   *    (manual review) instead of a resubmission loop.
   *  - **Image of the physical card** → the QR is on the inner page, so this image is
   *    NOT a usable front either way. Route into the normal front→back capture, which
   *    Veriff approves. Also the conservative choice when the CNN couldn't answer
   *    (`cnnType === null`): the user can still complete it, or restart with the PDF.
   *
   * Signature status remains authoritative-when-verified, advisory-when-not:
   *  - `verified`    — checked against a bundled SERPRO cert.
   *  - `invalid`     — decoded but matched no bundled cert. NOT a reliable forgery
   *                    signal (legacy RSA docs, or a retired/not-yet-rebundled SERPRO
   *                    key, still yield genuine current CNH-e). Proceed, flagged for
   *                    manual review by the provider.
   *  - `unverified`  — present but the cert couldn't be fetched (offline / CORS / SERPRO down).
   *  - `unsupported` — cert format we can't check.
   */
  onDigital(p: {
    doc: ParsedDocument;
    dataUrl: string;
    source: 'pdf' | 'image';
    cnnType: string | null;
    rawStorageBlob?: Blob;
    rawStorageName?: string;
    fileMetadata?: FileMetadata;
  }): void {
    const { doc, dataUrl, source, cnnType } = p;
    const sig = doc.signature;
    const ehCnh = docFamily(doc.kind) === 'cnh';

    if (ehCnh && sig?.status === 'invalid') {
      // QR decoded but didn't verify — proceed, flagged downstream for manual review.
      this.dbg('warn', 'signature', 'digital CNH signature invalid: unverifiable, proceeding for manual review', {
        kind: doc.kind,
        signature: sig.status,
      });
    }

    // A picture of a digital CNH: the QR is authoritative, the IMAGE is not. Proceed on
    // the data and mark the capture so the check is postponed rather than bounced back
    // to the user, who has nothing better to send (the photo is all they have to hand).
    if (ehCnh && source === 'image' && isDigital(cnnType ?? '')) {
      this.fotoDeCnhDigital = true;
      this.dbg('warn', 'digital', 'picture of a digital CNH — proceeding on the QR (Veriff will reject; postpone covers it)', {
        kind: doc.kind,
        cnnType,
        signature: sig?.status ?? 'none',
      });
      this.emitAction('digital-picture-accepted', { type: doc.kind, cnnType, signature: sig?.status ?? null });
      this.adotarDocDigital(doc, dataUrl, p);
      this.leuQr = true;
      this.montarCampos();
      this.mostrarResultado();
      try { this.cdr.detectChanges(); } catch {}
      return;
    }

    this.adotarDocDigital(doc, dataUrl, p);

    // A genuine CNH-e export (PDF + signed QR) IS the whole document — one sheet, no
    // physical back, nothing left to photograph. Finish on it.
    if (source === 'pdf') {
      this.leuQr = true;
      this.dbg('info', 'digital', 'genuine CNH-e export (signed PDF) — proceeding, no back or QR step', {
        kind: doc.kind,
        signature: sig?.status ?? 'none',
      });
      this.emitAction('digital-export-accepted', { type: doc.kind, digital: true, signature: sig?.status ?? null });
      this.montarCampos();
      this.mostrarResultado();
      try { this.cdr.detectChanges(); } catch {}
      return;
    }

    // An IMAGE whose signed QR decoded, that is not a picture of a digital CNH: it is a
    // photo of the physical card. The QR lives on the inner page (CNH) or the outer back
    // (CIN), so this image is never a usable front — collect the real sides. Also the
    // path taken when the CNN couldn't answer: capturing both sides still passes Veriff,
    // whereas guessing "digital" would postpone a check that could have auto-approved.
    // (CPK-4011: finishing here submitted a lone back image → "document not recognised".)
    const qrSurface = metaFor(doc.kind).qr;
    this.frentePareceVerso = qrSurface === 'back' || qrSurface === 'inner';
    this.dbg('info', 'digital', 'photo of a physical document — collecting front and back', {
      kind: doc.kind,
      cnnType,
      qrSurface,
    });
    this.emitAction('front-captured', { type: doc.kind, digital: true, needsBack: true, cnnType });
    this.step = 'confere-frente';
    this.speakCurrent();
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Adopt a decoded digital document as the current capture (fields, photo, front image). */
  private adotarDocDigital(
    doc: ParsedDocument,
    dataUrl: string,
    p: { rawStorageBlob?: Blob; rawStorageName?: string; fileMetadata?: FileMetadata },
  ): void {
    this.docDigital = doc;
    this.qrKind = doc.kind;
    this.qrVersionVerified = doc.versionVerified ?? null;
    this.qrFields = doc.fields;
    this.fotoPessoa = doc.photo?.dataUrl ?? null;
    this.assinatura = doc.signature ?? null;
    // Minimal capture record so the result shows the page image + a sane icon
    // (doc.kind is 'cin'/'cnh', which the doc-meta table already keys on).
    this.frente = {
      type: doc.kind,
      label: doc.title,
      confidence: 'high',
      recognized: true,
      processedDataUrl: dataUrl,
      processed: new Uint8Array(),
      ocrText: '',
      rawDataUrl: dataUrl,
      rawStorageBlob: p.rawStorageBlob,
      rawStorageName: p.rawStorageName,
      fileMetadata: p.fileMetadata,
    };
  }

  /**
   * User accepted the front photo → decide what comes next.
   *
   * If the document type couldn't be recognised (classifier `unknown`, or a
   * family we have no {@link DOC_META} for), we do **not** silently fall through
   * to the no-back default: we ask the user to pick the document type so
   * `temVerso`/`qr` are known and the back-capture logic runs correctly.
   */
  aceitarFrente(): void {
    const f = this.frente!;
    if (!this.tipoConhecido(f)) {
      // Bug #6: do NOT emit `front-accepted` here when routing to the manual type
      // picker — `escolherTipo()` emits it once after the pick. Emitting in both
      // places double-counted every unrecognized front. The emit below covers the
      // recognized path (and the wrong-side "keep as front" path via manterComoFrente).
      this.step = 'tipo-doc';
      this.confirmandoOutro = false; // always open on the document list, not the disclaimer
      this.speakCurrent();
      try { this.cdr.detectChanges(); } catch {}
      return;
    }
    this.emitAction('front-accepted');
    this.prosseguirAposFrente();
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Wrong-side keep (bug #1): the classifier flagged the captured "front" as a
   * back (`<fam>-back`) but the user insists it is the front and taps continue.
   * Relabel it to `<fam>-front` BEFORE proceeding so field extraction
   * (`TYPE_FIELDS` keys on `-front`) and the back/QR side logic aren't inverted.
   */
  protected manterComoFrente(): void {
    const f = this.frente;
    if (f && f.recognized && f.type.endsWith('-back')) {
      this.relabelSide(f, 'front');
    }
    this.frentePareceVerso = false;
    this.aceitarFrente();
  }

  /** Rewrite a capture's classifier type to the given side, preserving the family. */
  private relabelSide(r: BrdocsCaptureResult, side: 'front' | 'back'): void {
    r.type = `${docFamily(r.type)}-${side}`;
  }

  /** True when we know the document family (recognised AND present in DOC_META). */
  private tipoConhecido(f: BrdocsCaptureResult): boolean {
    return f.recognized && DOC_META[docFamily(f.type)] != null;
  }

  /**
   * The user picked the document type manually (fallback picker). Adopt the
   * chosen family as the effective type so every downstream step — `metaFor`,
   * `precisaVerso`/`temVerso`/`qr`, field extraction, the result — behaves
   * exactly as if the classifier had recognised it, then continue the normal
   * post-front flow (which requests the back for two-sided docs).
   */
  protected escolherTipo(familia: string): void {
    const f = this.frente;
    if (!f) return;
    this.confirmandoOutro = false;
    // Bug #8: mark it as a physically-captured FRONT (`<familia>-front`, e.g.
    // `outro-front`), not a bare family key, so `TYPE_FIELDS`/`precisaVerso`/
    // `isDigital` all behave exactly as for a classifier-recognised front.
    f.type = `${familia}-front`;
    f.recognized = true;
    this.frentePareceVerso = false;
    this.dbg('info', 'classify', `document type chosen manually: ${familia}`, { familia });
    this.emitAction('front-accepted', { type: familia, manual: true });
    this.prosseguirAposFrente();
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * The user tapped "Other / not listed": before adopting the generic `outro`
   * family, show the disclaimer that we may need to contact them for another ID.
   * Proceeding is an explicit second tap ("Prosseguir mesmo assim") →
   * {@link escolherTipo}`(OTHER_FAMILY)`.
   */
  protected pedirConfirmacaoOutro(): void {
    this.confirmandoOutro = true;
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Dismiss the "Other" disclaimer and return to the document-type list. */
  protected cancelarOutro(): void {
    this.confirmandoOutro = false;
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Post-front branching shared by the recognised path and the manual pick:
   * two-sided → capture the back; single sheet with a QR → scan it; otherwise
   * finish.
   */
  private prosseguirAposFrente(): void {
    const f = this.frente!;
    const m = metaFor(f.type);
    // The live QR-reading step needs the camera; when the user is uploading
    // files we read the QR straight from the uploaded image(s) in finalizar().
    const podeQrAoVivo = this.metodo === 'camera';
    if (precisaVerso(f.type)) {
      // Two-sided doc: always capture an actual PHOTO of the back — Veriff needs a
      // real back-of-document image. The QR (if any) is read afterwards in its own
      // focused `qr` step (see aceitarVerso), never conflated with this photo.
      this.step = 'verso-foto';
      this.speakCurrent();
    } else if (m.qr === 'front' && podeQrAoVivo) {
      // Single sheet with a front QR (certidão): scan the QR directly.
      this.irQr('frente');
    } else {
      void this.finalizar();
    }
  }

  /**
   * Enter the live QR-reading step, aimed at the given surface. Arms the delayed
   * "skip" escape: it appears only after {@link ATRASO_PULAR_MS} on screen.
   */
  private irQr(side: 'frente' | 'verso' | 'interna'): void {
    this.qrSide = side;
    this.step = 'qr';
    this.armarPular();
    this.speakCurrent();
  }

  /** Show the QR-step skip button only after the user has had time to actually scan. */
  private armarPular(): void {
    this.desarmarPular();
    this.podePular = false;
    this.pularTimer = setTimeout(() => {
      this.podePular = true;
      try { this.cdr.detectChanges(); } catch {}
    }, ATRASO_PULAR_MS);
  }

  /** Cancel the pending skip-button reveal (leaving the QR step, reset, destroy). */
  private desarmarPular(): void {
    if (this.pularTimer) clearTimeout(this.pularTimer);
    this.pularTimer = undefined;
    this.podePular = false;
  }

  /**
   * The live reader decoded a QR/MRZ → keep the data and show the result. The still
   * it grabbed is deliberately dropped: on a folded document it shows the inner
   * page, and it used to be adopted as the back image (see {@link versoImg}).
   */
  onQr(r: QrCaptureResult): void {
    this.leuQr = true;
    this.qrKind = r.doc.kind;
    this.qrVersionVerified = r.doc.versionVerified ?? null;
    this.qrFields = r.doc.fields;
    this.fotoPessoa = r.doc.photo?.dataUrl ?? null;
    this.assinatura = r.doc.signature ?? null;
    this.desarmarPular();
    this.emitAction('qr-read', { kind: r.doc.kind });
    this.montarCampos();
    this.mostrarResultado();
    try { this.cdr.detectChanges(); } catch {}
  }

  /** User couldn't read the QR (or doesn't have the document) → finish without it. */
  async pularQr(): Promise<void> {
    this.desarmarPular();
    this.emitAction('qr-skipped');
    this.step = 'processando';
    this.speakCurrent();
    // Last resort: try to find the QR in the photos we already have.
    await this.lerQr(metaFor(this.frente!.type));
    this.montarCampos();
    this.mostrarResultado();
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * The camera couldn't open for the QR step. For a folded document the QR lives on
   * a surface we have NOT photographed (the inner page), so there's nothing to fall
   * back to — send the user to the inner-page upload step instead of silently
   * finishing without the QR. Flat QR docs can still be read from the stills we hold.
   */
  onQrErro(): void {
    if (metaFor(this.frente!.type).qr === 'inner') {
      this.irInterna('upload');
      return;
    }
    void this.pularQr();
  }

  /** Enter the inner-page (QR surface) capture step with the given input method. */
  private irInterna(metodo: 'camera' | 'upload'): void {
    this.desarmarPular();
    this.metodo = metodo;
    this.step = 'interna-foto';
    this.speakCurrent();
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * The inner page (CNH QR/MRZ surface) was captured/picked. It is kept only so
   * `lerQr` can decode the QR out of it — it never reaches {@link buildResult}, so
   * it can't be mistaken for the document's back.
   */
  onInternaConfirmed(r: BrdocsCaptureResult): void {
    this.interna = r;
    this.emitAction('inner-captured', { type: r.type, recognized: r.recognized });
    void this.finalizar();
  }

  /**
   * The embedded camera couldn't be opened and the user chose "send a photo
   * instead". `brdocs-capture` binds `[source]` one-way, so we must mirror the
   * switch in our own method state — the same value `escolher('upload')` sets —
   * so `[source]` and `[digitalFirst]` re-derive to the upload path.
   */
  protected aoTrocarParaUpload(): void {
    this.metodo = 'upload';
    this.emitAction('method-selected', { method: 'upload', fromCameraFailure: true });
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Upload path + a document whose QR is on the surface we're photographing right
   * now (CIN: QR on the outer back). The back photo must show the QR fully and
   * sharply, because that same still is what we decode. Guidance only.
   *
   * A CNH is deliberately NOT included: its QR is on the inner page (`qr: 'inner'`),
   * a different surface entirely, so telling the user to "show the QR" on the back
   * step is exactly the confusion that made people photograph the inner page as the
   * back and get declined by Veriff with 609. The CNH reads its QR at its own
   * `interna-foto`/`qr` step instead.
   */
  protected get versoComQrUpload(): boolean {
    const f = this.frente;
    return this.metodo === 'upload' && !!f && metaFor(f.type).qr === 'back';
  }

  /**
   * Upload-path escape into a focused LIVE QR scan: reading a QR out of a still can
   * fail (glare, crop, blur), which drops the CPF. The live scan is full-res and
   * retryable.
   *
   * The back photo comes FIRST, always. CPK-4011: this escape used to sit on the
   * `verso-foto` step and jump straight to the QR reader, so a real back photo was
   * never captured and the submission fell back to the QR still as the document back
   * — a close-up of the QR, since the QR step asks the user to bring the code near the
   * camera. Veriff rejected every one of those. The button now lives on the
   * inner-page step, downstream of the back photo, and the guard below keeps that
   * guarantee even if it is ever placed on an earlier screen again.
   */
  protected escanearQrComCamera(): void {
    this.metodo = 'camera';
    this.emitAction('method-selected', { method: 'camera', toScanQr: true });
    if (!this.verso && precisaVerso(this.frente!.type)) {
      this.step = 'verso-foto'; // no back banked yet — capture it before any QR scan
      this.speakCurrent();
      try { this.cdr.detectChanges(); } catch {}
      return;
    }
    this.irQr(metaFor(this.frente!.type).qr === 'inner' ? 'interna' : 'verso');
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Retake the front. */
  refazerFrente(): void {
    this.frente = null;
    this.frentePareceVerso = false;
    this.step = 'frente';
    this.speakCurrent();
    this.emitAction('front-retaken');
    try { this.cdr.detectChanges(); } catch {}
  }

  /** The embedded camera confirmed the back photo. */
  onVersoConfirmed(r: BrdocsCaptureResult): void {
    if (this.documentCheck) {
      void this.checarDocumento(r, 'verso');
      return;
    }
    this.mostrarVerso(r);
  }

  /** Bank the back capture and show its "ficou boa?" preview. */
  private mostrarVerso(r: BrdocsCaptureResult): void {
    this.verso = r;
    this.step = 'confere-verso';
    this.speakCurrent();
    this.emitAction('back-captured', { type: r.type, recognized: r.recognized });
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * CPK-5103: ask the host whether this capture shows a document at all, before its preview.
   * The user waits on a spinner (no voice — a real document answers in about a second, and a
   * clip cut off mid-word is worse than silence). A refusal shows the `nao-documento` screen
   * with the image they sent; the Nth refusal in a row, or a block for too many refusals,
   * restarts the flow (the host explains why). Fail-open: any rejection from the check counts
   * as a document.
   */
  private async checarDocumento(r: BrdocsCaptureResult, lado: 'frente' | 'verso'): Promise<void> {
    const check = this.documentCheck!;
    const id = ++this.checagemId;
    this.calar();
    this.step = 'processando';
    try { this.cdr.detectChanges(); } catch {}

    let outcome: DocumentCheckOutcome;
    try {
      outcome = await check({
        image: r.rawDataUrl,
        side: lado === 'frente' ? 'front' : 'back',
        method: this.metodo,
        source: ehPdf(r) ? 'pdf' : 'image',
      });
    } catch (err) {
      this.dbg('warn', 'is-document', 'is_document check failed — continuing to the preview (fail-open)', {
        side: lado,
        error: String(err),
      });
      outcome = 'document';
    }
    if (id !== this.checagemId || this.destruido) return; // restarted / torn down meanwhile

    this.dbg(outcome === 'document' ? 'info' : 'warn', 'is-document', `is_document → ${outcome}`, { side: lado, outcome });
    if (outcome === 'not_document') {
      this.naoDocumentoImg = r.rawDataUrl;
      this.naoDocumentoLado = lado;
      this.step = 'nao-documento';
      this.speakCurrent();
      this.emitAction('not-document', { side: lado });
      try { this.cdr.detectChanges(); } catch {}
      return;
    }
    if (outcome === 'limit_restart' || outcome === 'blocked') {
      this.emitAction(outcome === 'blocked' ? 'not-document-blocked' : 'not-document-limit', { side: lado });
      this.recomecar();
      return;
    }
    if (outcome === 'limit_proceed') this.emitAction('not-document-overridden', { side: lado });
    if (lado === 'frente') this.mostrarFrente(r);
    else this.mostrarVerso(r);
  }

  /** "Escolher outra" / "Tirar de novo" on the `nao-documento` screen: capture that side again. */
  protected tentarDeNovoNaoDocumento(): void {
    const lado = this.naoDocumentoLado;
    this.naoDocumentoImg = null;
    this.step = lado === 'frente' ? 'frente' : 'verso-foto';
    this.speakCurrent();
    this.emitAction(lado === 'frente' ? 'front-retaken' : 'back-retaken', { afterNotDocument: true });
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * User accepted the back photo — the last image Veriff needs. What follows depends
   * on where the document keeps its QR:
   *
   *  - `'inner'` (CNH): the QR is on a surface we haven't seen yet. Always take a
   *    third pass at it — live on the camera path, or as an uploaded photo of the
   *    opened document otherwise. The back photo is already banked, so whatever
   *    happens here can no longer corrupt it.
   *  - `'back'` (CIN): the QR is on the surface we just photographed. Camera path
   *    scans it live (full-res, rather than re-reading the framed-for-legibility
   *    still); the upload path decodes it from that still in `finalizar`.
   *  - no QR: just finish.
   */
  aceitarVerso(): void {
    this.emitAction('back-accepted');
    const m = metaFor(this.frente!.type);
    if (m.qr === 'inner') {
      if (this.metodo === 'camera') this.irQr('interna');
      else this.irInterna('upload');
      try { this.cdr.detectChanges(); } catch {}
      return;
    }
    if (m.qr === 'back' && this.metodo === 'camera') {
      this.irQr('verso');
      try { this.cdr.detectChanges(); } catch {}
      return;
    }
    void this.finalizar();
  }

  /** Retake the back. */
  refazerVerso(): void {
    this.verso = null;
    this.step = 'verso-foto';
    this.speakCurrent();
    this.emitAction('back-retaken');
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Start over from the welcome screen (replays the welcome clip). */
  recomecar(): void {
    this.reset();
    this.step = 'inicio';
    this.speakCurrent();
    this.emitAction('restarted');
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Where the top-bar back button returns to from each screen. Entry (`inicio`),
   * the transient `processando`, and the terminal `resultado` have no previous
   * screen and so show no back button.
   */
  private readonly passoAnterior: Partial<Record<Step, Step>> = {
    escolha: 'inicio',
    frente: 'escolha',
    'confere-frente': 'frente',
    'tipo-doc': 'confere-frente',
    'verso-foto': 'confere-frente',
    'confere-verso': 'verso-foto',
    'interna-foto': 'confere-verso',
    // `qr` is dynamic (see alvoAnterior): back to the back review for two-sided QR
    // docs, or the front review for single-sheet front-QR docs.
    'pdf-erro': 'frente',
  };

  /**
   * The screen the back button returns to. Static for most steps; `qr` is dynamic
   * because it's reached after the back photo (outer-back QR docs, and folded docs
   * whose inner page comes next) or straight after the front (single-sheet front-QR
   * docs like certidão).
   */
  private alvoAnterior(): Step | undefined {
    // Back from "not a document" = capture the refused side again (same as its button).
    if (this.step === 'nao-documento') return this.naoDocumentoLado === 'frente' ? 'frente' : 'verso-foto';
    if (this.step === 'qr') {
      if (this.qrSide === 'verso' || this.qrSide === 'interna') {
        // The back photo is mandatory before either of these, so `verso` is normally
        // set; stay defensive and fall back to the capture step rather than the review
        // screen (which renders `verso!.rawDataUrl`) if it somehow isn't.
        return this.verso ? 'confere-verso' : 'verso-foto';
      }
      return 'confere-frente';
    }
    return this.passoAnterior[this.step];
  }

  /** True when the current screen has a previous screen to step back to. */
  protected get podeVoltar(): boolean {
    return this.alvoAnterior() != null;
  }

  /**
   * Step back one screen, undoing the capture state the screen we're leaving
   * produced so the target screen is consistent (mirrors the per-screen "retake"
   * actions). No-op on screens without a previous screen.
   */
  voltar(): void {
    // On the "Other / not listed" disclaimer, back returns to the type list —
    // not the previous wizard screen.
    if (this.step === 'tipo-doc' && this.confirmandoOutro) {
      this.cancelarOutro();
      return;
    }
    const alvo = this.alvoAnterior();
    if (!alvo) return;
    switch (this.step) {
      case 'confere-frente':
        this.frente = null; // back to capturing the front
        break;
      case 'verso-foto':
        this.verso = null; // back to the front review; drop any pending back capture
        break;
      case 'qr':
        // Back to reviewing the back photo (two-sided) or the front (single-sheet);
        // the captured back photo is kept — it's what Veriff needs and it is never
        // derived from anything on this screen.
        this.desarmarPular();
        break;
      case 'interna-foto':
        this.interna = null; // back to the back review; drop any pending inner-page capture
        break;
      case 'confere-verso':
        this.verso = null; // back to recapturing the back
        break;
      case 'pdf-erro':
        this.docDigital = null; // back to re-upload the document
        this.frente = null;
        break;
      case 'nao-documento':
        this.naoDocumentoImg = null; // the refused capture was never banked
        break;
    }
    this.step = alvo;
    this.speakCurrent();
    this.emitAction('navigated-back');
    try { this.cdr.detectChanges(); } catch {}
  }

  // --- Compilation ----------------------------------------------------------

  /**
   * Finish the capture: if the document carries a QR, read and verify it; then
   * merge the QR fields with the best-effort OCR fields and show the result.
   */
  private async finalizar(): Promise<void> {
    const f = this.frente!;
    const m = metaFor(f.type);
    this.aviso = null;

    if (m.qr !== 'none') {
      this.step = 'processando';
      this.speakCurrent();
      try { this.cdr.detectChanges(); } catch {}
      // Breadcrumb: attribute a freeze during the QR read to `read_qr`.
      await this.watchdog.setPhase('read_qr', { qr: m.qr, method: this.metodo, family: docFamily(f.type) });
      await this.lerQr(m);
    }

    this.montarCampos();
    this.mostrarResultado();
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Show the compiled result. No voiceover or "processed" snackbar here: at this
   * point the document has only been READ — the host still runs its identity
   * pre-check, so announcing "pronto, estas são as informações do seu documento"
   * (spoken + toast) would confirm success too soon. The result screen is shown
   * silently and the host decides what to confirm.
   */
  private mostrarResultado(): void {
    this.step = 'resultado';
    const result = this.buildResult();
    this.emitAction('completed', { type: result.type, recognized: result.recognized });
    this.completed.emit(result);
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Assemble the public {@link CaptureFlowResult} from the current state. */
  private buildResult(): CaptureFlowResult {
    const f = this.frente;
    return {
      type: this.docDigital?.kind ?? f?.type ?? 'unknown',
      documentName: this.nomeDoc,
      recognized: this.docDigital != null || !!f?.recognized,
      fields: this.campos.map((c) => ({ label: c.label, value: c.value, source: c.fonte })),
      photo: this.fotoPessoa,
      signature: this.assinatura,
      fromQrCode: this.leuQr,
      qrKind: this.qrKind,
      versionVerified: this.qrVersionVerified,
      frontImage: f?.rawDataUrl ?? null,
      backImage: this.versoImg,
      warning: this.aviso,
      method: this.metodo,
      frontRaw: this.frente?.rawStorageBlob ?? null,
      frontRawName: this.frente?.rawStorageName ?? null,
      frontMeta: this.frente?.fileMetadata ?? null,
      backRaw: this.verso?.rawStorageBlob ?? null,
      backRawName: this.verso?.rawStorageName ?? null,
      backMeta: this.verso?.fileMetadata ?? null,
      digitalPicture: this.fotoDeCnhDigital,
    };
  }

  /** Try to decode a QR from the captured surfaces (the QR-bearing one first). */
  private async lerQr(m: DocMeta): Promise<void> {
    const f = this.frente;
    const v = this.verso;
    const i = this.interna;
    // Prefer the surface the QR actually lives on; fall back to the others. For a
    // folded document that surface is the inner page — never the outer back, which
    // carries no QR at all.
    const sides =
      m.qr === 'inner'
        ? [i?.rawDataUrl, v?.rawDataUrl, f?.rawDataUrl]
        : m.qr === 'back'
          ? [v?.rawDataUrl, f?.rawDataUrl]
          : [f?.rawDataUrl, v?.rawDataUrl];

    // Scan one side at a time, releasing each full-resolution canvas before the
    // (async, off-main-thread) decode so two ~18 MP buffers are never alive at once
    // — the tab-freeze / OOM after the back upload came from doing this on the UI
    // thread with both sides' buffers retained. `scanPixelsHeavy` decodes the QR in
    // a Web Worker (transferring the pixel buffer off the main thread) and parses on
    // the main thread; behaviour is identical to the old `scanPixels`.
    for (const url of sides) {
      if (!url) continue;
      let canvas: HTMLCanvasElement | null = null;
      try {
        canvas = await dataUrlToCanvas(url);
        const pixels = canvasToPixels(canvas);
        // Free the big canvas now; the pixel buffer is transferred to the worker.
        canvas.width = canvas.height = 0;
        canvas = null;
        const res = await this.scanner.scanPixelsHeavy(pixels);
        if (res.document) {
          this.leuQr = true;
          this.qrKind = res.document.kind;
          this.fotoPessoa = res.document.photo?.dataUrl ?? null;
          this.assinatura = res.document.signature ?? null;
          this.qrFields = res.document.fields;
          this.dbg('info', 'qr', `QR found in captured photo: ${res.document.kind}`, {
            kind: res.document.kind,
            signature: res.document.signature?.status,
          });
          return;
        }
      } catch {
        // keep trying the other side
      } finally {
        if (canvas) canvas.width = canvas.height = 0;
      }
    }
    this.dbg('warn', 'qr', 'no QR found in captured photos — using OCR fields only');
    this.aviso =
      'Não consegui ler o código QR. Os dados abaixo foram lidos do texto e podem conter erros.';
  }

  /** Fields decoded from the QR, kept between {@link lerQr} and {@link montarCampos}. */
  private qrFields: DocField[] = [];

  /** Merge QR fields (trustworthy) with OCR fields (best-effort) for display. */
  private montarCampos(): void {
    const out: ResultField[] = [];
    const seen = new Set<string>();

    for (const fld of this.qrFields) {
      if (!fld.value?.trim()) continue;
      out.push({ label: fld.label, value: fld.value, fonte: 'qr' });
      seen.add(norm(fld.label));
    }

    // OCR from every surface we captured (a folded doc's inner page carries the MRZ),
    // normalised per document type.
    const f = this.frente;
    const text = [this.frente?.ocrText, this.verso?.ocrText, this.interna?.ocrText]
      .filter(Boolean)
      .join('\n');
    const ocr = f ? extractFields(text, f.type) : {};
    for (const [key, value] of Object.entries(ocr)) {
      if (!value?.trim()) continue;
      const label = OCR_LABELS[key] ?? key;
      if (seen.has(norm(label))) continue; // already have it from the QR
      out.push({ label, value, fonte: 'ocr' });
      seen.add(norm(label));
    }

    this.campos = out;
    this.dbg('info', 'pipeline', `compiled ${out.length} fields`, {
      total: out.length,
      fromQr: out.filter((c) => c.fonte === 'qr').length,
      fromOcr: out.filter((c) => c.fonte === 'ocr').length,
      signature: this.assinatura?.status ?? null,
    });
    try { this.cdr.detectChanges(); } catch {}
  }

  // --- Voice ----------------------------------------------------------------

  /** Toggle spoken instructions; speak the current screen when turning on. */
  alternarVoz(): void {
    const on = !this.voz;
    this.voz = on;
    if (on) {
      const { id, fala } = this.audioInfo();
      this.tocar(id, fala);
    } else {
      this.calar();
    }
    this.emitAction('voice-toggled', { on });
  }

  /**
   * The clip id + spoken text for the current screen. Keyed by step **and**
   * method so the words match the action: e.g. the upload front/back screens say
   * "choose the photo on your phone", not "show it to the camera".
   */
  private audioInfo(): { id: string; fala: string } {
    const s = this.step;
    if (this.metodo === 'upload') {
      switch (s) {
        case 'frente':
          return {
            id: 'frente-upload',
            fala: 'Escolha o documento no seu aparelho. Pode ser uma foto do documento, ou o arquivo de um documento digital, como um PDF.',
          };
        case 'verso-foto':
          // Bug #4: on the upload path for a doc whose QR is on the back it photographs
          // (CIN) the spoken guidance must again warn that the QR has to be fully
          // visible, sharp and glare-free — voice is the primary channel for
          // low-literacy users. (The on-screen QR hint is shown separately via
          // `BrdocsUploadBackQrGuidance`.)
          if (this.versoComQrUpload) {
            return {
              id: 'verso-qr-upload',
              fala: 'Agora escolha a foto do outro lado do documento. É muito importante que o quadradinho cheio de pontos, o QR, apareça inteiro, bem nítido, sem brilho e sem reflexo. Se ele estiver cortado ou embaçado, eu não vou conseguir ler.',
            };
          }
          // A folded document (CNH) must NOT hear "show the QR" here: its QR is on the
          // inner page, a different surface, and sending that page as the back is what
          // Veriff declines with "Document back missing". Ask for the closed document's
          // categories side, and say plainly that this is not the QR page yet.
          if (metaFor(this.frente?.type ?? '').qr === 'inner') {
            return {
              id: 'verso-dobravel-upload',
              fala: 'Agora escolha a foto do outro lado do documento, com ele fechado: o lado das categorias, onde ficam as letras A, B e C. Ainda não é a parte do QR — a gente vai chegar nela daqui a pouco.',
            };
          }
          return {
            id: 'verso-upload',
            fala: 'Agora escolha a foto do outro lado do documento. O verso é a parte de trás.',
          };
        case 'interna-foto':
          return {
            id: 'interna-upload',
            fala: 'Agora abra o documento e vire. Escolha a foto da parte de dentro, a página do QR: aquele quadradinho cheio de pontos.',
          };
        case 'confere-frente':
          return {
            id: 'confere-frente-upload',
            fala: 'A imagem ficou boa? Se der para ler tudo, toque em Sim. Se não, escolha outra imagem.',
          };
        case 'confere-verso':
          return {
            id: 'confere-verso-upload',
            fala: 'A imagem do outro lado ficou boa? Se sim, toque em Sim. Se não, escolha outra imagem.',
          };
        case 'nao-documento':
          return {
            id: 'nao-documento-upload',
            fala: 'Não encontramos um documento nesta imagem. Envie um arquivo do seu RG ou da sua CNH, mostrando o documento inteiro.',
          };
      }
    }
    // Camera path, folded document: the generic "vire o documento" clip is what let
    // people turn to the QR page and hand us the wrong surface. Name the categories
    // side and rule the QR page out, exactly as the screen does.
    if (s === 'verso-foto' && metaFor(this.frente?.type ?? '').qr === 'inner') {
      return {
        id: 'verso-dobravel',
        fala: 'Agora vire o documento sem abrir, e mostre o lado das categorias, onde ficam as letras A, B e C. Ainda não é a parte do QR — a gente vai chegar nela daqui a pouco. Segure firme.',
      };
    }
    return { id: s, fala: GUIA[s].fala };
  }

  /**
   * Speak the guidance for a screen by playing its pre-recorded neural clip
   * (`public/audio/<id>.mp3`, generated by edge-tts). We **never** fall back to
   * the browser/OS `speechSynthesis` voice — it sounds robotic. If a clip is
   * missing or autoplay is blocked, the screen stays silent (the on-screen text
   * still guides the user). When the first clip is autoplay-blocked we re-arm it
   * to play on the next user gesture, so guidance resumes once interaction starts.
   * Best-effort: never throws.
   */
  private tocar(id: string, _fala: string): void {
    if (!this.voz) return;
    // The result screen is intentionally silent: the document has only been READ, not verified, so
    // we must not announce "pronto, estas são as informações do seu documento" before the host's
    // identity pre-check runs. Covers every caller (auto-advance AND the toggle-voice button).
    if (id === 'resultado') {
      this.calar();
      return;
    }
    this.calar(); // stop any clip still playing from the last screen
    const reqId = ++this.faladaId;
    try {
      const el = (this.audio ??= new Audio());
      el.onerror = null; // missing file → silent, no robotic fallback
      el.src = `assets/brdocs/audio/${id}.mp3`;
      el.currentTime = 0;
      void el.play().catch(() => this.armarGesto(reqId)); // autoplay blocked → retry on gesture
    } catch {
      // silent — guidance is optional
    }
  }

  /**
   * Autoplay can block the very first clip (no user gesture yet). Re-attempt the
   * same clip once, on the next pointer/key/touch, then detach. Still the neural
   * clip — never the browser voice.
   */
  private armarGesto(reqId: number): void {
    if (this.gestoArmado) return;
    this.gestoArmado = true;
    const retry = () => {
      remove();
      if (reqId === this.faladaId) this.audio?.play().catch(() => {});
    };
    const remove = () => {
      this.gestoArmado = false;
      window.removeEventListener('pointerdown', retry);
      window.removeEventListener('keydown', retry);
      window.removeEventListener('touchstart', retry);
    };
    window.addEventListener('pointerdown', retry, { once: true });
    window.addEventListener('keydown', retry, { once: true });
    window.addEventListener('touchstart', retry, { once: true });
  }

  /** Stop any spoken guidance (the recorded clip). */
  private calar(): void {
    this.faladaId++;
    this.audio?.pause();
  }

  /** Monotonic id so a late clip retry can't talk over a newer screen. */
  private faladaId = 0;
  private gestoArmado = false;

  // --- Helpers --------------------------------------------------------------

  /** Visible heading/body text for the current step (QR text adapts to the side). */
  protected get guia(): { titulo: string; corpo: string } {
    const s = this.step;
    // When uploading, the front/back screens guide a file pick, not a photo. The
    // front can be a photo OR a digital file (PDF); the back step is only reached
    // for physical two-sided documents, so it stays photo-oriented.
    if (s === 'frente' && this.metodo === 'upload') {
      return {
        titulo: 'Envie o documento',
        corpo: 'Escolha no seu aparelho a foto do documento ou o arquivo em PDF.',
      };
    }
    if (s === 'verso-foto') {
      // A folded document has THREE surfaces, and the two the user can mix up are the
      // outer back and the inner QR page. Name the back by what is printed on it — the
      // vehicle categories — and rule the QR page out in so many words, on both paths.
      // Sending the QR page as the back is exactly what Veriff rejects with 609.
      const dobravel = metaFor(this.frente?.type ?? '').qr === 'inner';
      if (dobravel) {
        return {
          titulo: 'Agora o VERSO (sem abrir)',
          corpo:
            this.metodo === 'upload'
              ? 'Com o documento FECHADO, envie a foto do outro lado — o lado das categorias (A, B, C…). Ainda NÃO é a parte do QR.'
              : 'Vire o documento SEM abrir. É o lado das categorias (A, B, C…). Ainda NÃO é a parte do QR.',
        };
      }
      if (this.metodo === 'upload') {
        // "VERSO = the other side" is stated once by the illustration caption below.
        return {
          titulo: 'Agora envie o VERSO',
          corpo: 'Escolha no seu aparelho a foto desse lado.',
        };
      }
    }
    if (s === 'interna-foto') {
      return {
        titulo: 'Agora ABRA o documento',
        corpo:
          this.metodo === 'upload'
            ? 'Abra o documento, vire, e envie a foto da parte de dentro — a página do QR.'
            : 'Abra o documento e vire para mostrar a página do QR.',
      };
    }
    // Review screens: uploaders pick another file, they don't "take it again".
    // Buttons carry Sim/"Escolher outra", so the body only gives the check.
    if ((s === 'confere-frente' || s === 'confere-verso') && this.metodo === 'upload') {
      return {
        titulo: 'A imagem ficou boa?',
        corpo: 'Veja se dá para ler tudo.',
      };
    }
    if (s === 'tipo-doc') {
      // Fallback manual type picker — user-facing copy via i18n keys. Once the
      // user taps "Other / not listed", the header carries the disclaimer copy.
      if (this.confirmandoOutro) {
        return {
          titulo: this.translate.instant('BrdocsManualTypeOtherDisclaimerTitle'),
          corpo: this.translate.instant('BrdocsManualTypeOtherDisclaimer'),
        };
      }
      return {
        titulo: this.translate.instant('BrdocsManualTypeTitle'),
        corpo: this.translate.instant('BrdocsManualTypeBody'),
      };
    }
    if (s === 'pdf-erro') {
      // Terminal card for a digital CNH that isn't a genuine signed export.
      return {
        titulo: this.translate.instant('BrdocsPdfErroTitle'),
        corpo: this.translate.instant('BrdocsPdfErroBody'),
      };
    }
    if (s === 'nao-documento') {
      // The guidance sits UNDER the refused image (template), so the header carries the title only.
      return { titulo: this.translate.instant('BrdocsNotDocumentTitle'), corpo: '' };
    }
    if (s === 'qr') {
      // The CNH is folded: the user must OPEN it and turn it so the inner QR page faces
      // the camera. Other QR docs (CIN back, certidão front) are flat.
      // "The QR is the little square full of dots" is stated once by the illustration
      // caption below; the body carries only the positioning action for this document.
      const ondeEsta =
        this.qrSide === 'interna'
          ? 'Abra o documento, vire, e aproxime a página do QR da câmera.'
          : this.qrSide === 'verso'
            ? 'Vire o documento e aproxime o lado do QR da câmera.'
            : 'Aproxime o QR da câmera.';
      return { titulo: 'Agora vamos ler o QR', corpo: ondeEsta };
    }
    return GUIA[s];
  }

  /** Redo button on the review screens: retake a photo, or pick another file. */
  protected get rotuloRefazer(): { texto: string; icon: string } {
    return this.metodo === 'upload'
      ? { texto: 'Escolher outra', icon: 'upload_file' }
      : { texto: 'Tirar de novo', icon: 'replay' };
  }

  /**
   * The capture stages for the progress dots and which one is active. Two-sided QR
   * docs on the camera path are now three logical stages — front / back / QR — so
   * the count can be 1, 2 or 3.
   */
  protected get etapas(): { total: number; atual: number } {
    const t = this.frente?.type;
    if (!t) return { total: 1, atual: 1 };
    const m = metaFor(t);
    const temVersoFoto = precisaVerso(t);
    // The QR is a logical stage whenever the DOCUMENT plan has one — independent of
    // input method. On camera it's a dedicated live `qr` screen; on upload it's read
    // from the captured stills (no screen), but the stage still exists. Counting it
    // from the plan (not `metodo`) keeps the dot count stable across a camera→upload
    // switch (aoTrocarParaUpload), so the dots never shrink mid-capture.
    const temQrStep = m.qr === 'back' || m.qr === 'front' || m.qr === 'inner';
    let total = 1;
    if (temVersoFoto) total++;
    if (temQrStep) total++;

    let atual = 1;
    if (this.step === 'interna-foto') {
      atual = total; // the inner page IS the QR stage — the last one
    } else if (this.step === 'verso-foto') {
      atual = temVersoFoto ? 2 : 1;
    } else if (this.step === 'confere-verso') {
      // Bug #5 (phantom QR dot on upload): the QR stage is counted from the doc plan
      // (kept method-independent so the dot count doesn't shrink 3→2 on a camera→upload
      // switch). When the QR is read INLINE with no screen of its own — the upload path
      // for a doc whose QR is on the back we just confirmed — fill the QR dot here too,
      // or it sits perpetually unfilled and looks stuck. A folded document (`inner`) is
      // excluded: its QR page is a real screen still to come, on either path, so the dot
      // must stay pending.
      const qrLidoInline = this.metodo === 'upload' && m.qr === 'back';
      atual = temVersoFoto ? (qrLidoInline ? total : 2) : 1;
    } else if (this.step === 'qr') {
      atual = total; // the QR is always the last stage
    } else if (this.step === 'nao-documento') {
      // Still on the stage whose capture was refused. (A refused front never set `frente`, so
      // that case already returned 1/1 above.)
      atual = this.naoDocumentoLado === 'verso' && temVersoFoto ? 2 : 1;
    }
    return { total, atual };
  }

  /** Whether the top-bar progress dots are shown (only on capture stages). */
  protected get mostrarDots(): boolean {
    return (
      this.step === 'frente' ||
      this.step === 'confere-frente' ||
      this.step === 'verso-foto' ||
      this.step === 'confere-verso' ||
      this.step === 'nao-documento' ||
      this.step === 'qr'
    );
  }

  /** One entry per capture stage; `true` = reached/active, for the progress dots. */
  protected get dots(): boolean[] {
    const { total, atual } = this.etapas;
    return Array.from({ length: total }, (_, i) => i < atual);
  }

  /** Friendly description of the signature status. */
  protected statusAssinatura(s: DocSignature): { texto: string; icon: string; cls: string } {
    switch (s.status) {
      case 'verified':
        return { texto: 'Documento autêntico (assinatura verificada)', icon: 'verified', cls: 'ok' };
      case 'invalid':
        return { texto: 'Atenção: assinatura não confere', icon: 'gpp_bad', cls: 'bad' };
      default:
        return { texto: 'Documento com assinatura (não verificada)', icon: 'gpp_maybe', cls: 'warn' };
    }
  }

  private reset(): void {
    this.calar();
    this.desarmarPular();
    this.metodo = 'camera';
    this.docDigital = null;
    this.frente = null;
    this.verso = null;
    this.interna = null;
    this.fotoDeCnhDigital = false;
    this.frentePareceVerso = false;
    this.campos = [];
    this.fotoPessoa = null;
    this.assinatura = null;
    this.leuQr = false;
    this.qrKind = null;
    this.aviso = null;
    this.qrFields = [];
    this.naoDocumentoImg = null;
    this.naoDocumentoLado = 'frente';
    this.checagemId++; // an is_document answer still in flight no longer applies
  }

  /** Silence any guidance when the tab/component goes away. */
  ngOnDestroy(): void {
    this.destruido = true;
    this.calar();
    this.desarmarPular();
    this.cameraStartedSub?.unsubscribe();
    this.watchdog.stop();
  }
}

/**
 * How long the QR step waits before offering a way out. The scan is worth a real
 * attempt — a signed QR is the only authoritative source of the CPF — so the skip
 * button is withheld until the user has plainly been trying, rather than sitting
 * there as the easiest thing to tap on arrival.
 */
const ATRASO_PULAR_MS = 10_000;

/** Per-step on-screen + spoken guidance, all pt-BR. */
const GUIA: Record<Step, { titulo: string; corpo: string; fala: string }> = {
  inicio: {
    titulo: 'Vamos ler o seu documento',
    corpo: 'É rápido e fácil. Toque no botão verde.',
    fala: 'Olá! Vamos ler o seu documento. Toque no botão verde para começar.',
  },
  escolha: {
    // The two big labelled buttons ("Tirar foto agora" / "Enviar do meu aparelho")
    // already state the choice, so no body text is needed — the empty <p> is hidden.
    titulo: 'Como você quer enviar o documento?',
    corpo: '',
    fala: 'Como você quer enviar o documento? Tire uma foto agora, ou escolha uma foto que já está no seu aparelho.',
  },
  frente: {
    // "Front = the photo side" is stated once by the on-screen illustration caption
    // (and the spoken guidance); the title just names the step and the body carries
    // only the action, so the fact isn't repeated three times.
    titulo: 'Mostre a FRENTE do documento',
    corpo: 'Coloque dentro da moldura e segure firme.',
    fala: 'Mostre a frente do documento, o lado onde fica a sua foto. Segure firme, a foto é tirada sozinha.',
  },
  'confere-frente': {
    // The two buttons say "Sim, está boa" / "Tirar de novo", so the body only gives
    // the one thing to check.
    titulo: 'A foto ficou boa?',
    corpo: 'Veja se dá para ler tudo.',
    fala: 'A foto ficou boa? Se der para ler tudo, toque em Sim. Se não, tire de novo.',
  },
  // Fallback manual type picker. Visible title/body come from i18n keys (see the
  // `guia` getter); this entry supplies the type completeness for GUIA and the
  // narration-clip id/text (`assets/brdocs/audio/tipo-doc.mp3`; silent if absent).
  'tipo-doc': {
    titulo: 'Qual é o documento?',
    corpo: 'Não consegui reconhecer o documento. Toque no tipo do seu documento na lista.',
    fala: 'Não consegui reconhecer o documento. Toque no tipo do seu documento na lista.',
  },
  'verso-foto': {
    // "VERSO = the other side" is stated once by the illustration caption; the title
    // just names the step and the body carries the action.
    titulo: 'Agora mostre o VERSO',
    corpo: 'Vire o documento e coloque na moldura. Segure firme.',
    fala: 'Agora vire o documento e mostre o verso, a parte de trás. Segure firme.',
  },
  'confere-verso': {
    // Same pattern as confere-frente: buttons carry Sim/refazer, body gives the check.
    titulo: 'A foto do outro lado ficou boa?',
    corpo: 'Veja se dá para ler tudo.',
    fala: 'A foto do outro lado ficou boa? Se sim, toque em Sim. Se não, tire de novo.',
  },
  // Third surface of a folded document (the CNH's inner QR/MRZ page). Reached only
  // after the outer back is banked, so nothing captured here can end up standing in
  // for the back. Visible title/body are specialised per method in the `guia` getter;
  // this entry supplies the narration clip id/text (silent if the clip is absent).
  'interna-foto': {
    titulo: 'Agora ABRA o documento',
    corpo: 'Abra o documento e vire para mostrar a parte de dentro — a página do QR.',
    fala: 'Agora abra o documento e vire, para mostrar a parte de dentro: a página do QR, aquele quadradinho cheio de pontos.',
  },
  qr: {
    titulo: 'Agora vamos ler o QR',
    corpo: 'O QR é o quadradinho cheio de pontos, geralmente atrás do documento. Aproxime ele da câmera.',
    fala: 'Agora vamos ler o QR. O QR é aquele quadradinho cheio de pontos, geralmente atrás do documento. Aproxime ele da câmera e segure firme.',
  },
  processando: {
    titulo: 'Lendo o documento…',
    corpo: 'Aguarde um instante.',
    fala: 'Aguarde um instante. Estou lendo o seu documento.',
  },
  resultado: {
    titulo: 'Pronto!',
    corpo: 'Estas são as informações do seu documento.',
    fala: 'Pronto! Estas são as informações do seu documento.',
  },
  // Terminal card for a PICTURE of a digital CNH whose QR we couldn't read — the one
  // case where the user must go and fetch a different file, because a photo of a screen
  // will never verify and, without the QR, we hold no data to proceed on either. Visible
  // title/body come from i18n keys (see the `guia` getter); this entry supplies the GUIA
  // type completeness + the narration-clip id/text (silent if the clip is absent).
  'pdf-erro': {
    titulo: 'Precisamos do PDF da sua CNH digital',
    corpo: 'Você enviou uma FOTO da CNH digital, e a foto não vale. Abra o aplicativo Carteira Digital de Trânsito, toque em Exportar e salve o PDF no seu aparelho. Depois toque em Enviar e escolha esse arquivo PDF — sem editar e sem tirar print.',
    fala: 'Você enviou uma foto da sua CNH digital, e a foto não vale. Precisamos do arquivo PDF. Abra o aplicativo Carteira Digital de Trânsito, aquele mesmo onde você vê a sua CNH, e toque na opção Exportar. Salve o arquivo PDF no seu aparelho. Depois, toque em Enviar e escolha esse arquivo PDF. Não edite o arquivo, e não tire print da tela.',
  },
  // CPK-5103: the is_document check found no document in the capture (a selfie, a blank photo,
  // a screenshot, a bill…). Visible text comes from i18n keys (`guia` getter + template); this
  // entry supplies GUIA's type completeness + the camera narration clip (the upload one is in
  // `audioInfo`). Silent if the clip is absent.
  'nao-documento': {
    titulo: 'Não encontramos um documento nesta imagem',
    corpo: '',
    fala: 'Não encontramos um documento nesta imagem. Tire uma foto do seu RG ou da sua CNH, mostrando o documento inteiro.',
  },
};

/** True when the capture came from an uploaded PDF (its image is the rendered first page). */
function ehPdf(r: BrdocsCaptureResult): boolean {
  return (
    r.fileMetadata?.file?.mimeType === 'application/pdf' ||
    /\.pdf$/i.test(r.rawStorageName ?? '')
  );
}

/**
 * True when, after the front, we should ask for the back: the document family
 * has a back side AND this capture isn't already a single-sheet variant — an
 * "open" capture (both sides at once) or a "-digital" printout (CNH-e / CIN
 * digital), which have no separate physical back.
 */
function precisaVerso(type: string): boolean {
  return metaFor(type).temVerso && !isOpen(type) && !isDigital(type);
}

/** Lowercase + strip accents/spaces, for de-duping labels across sources. */
function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/** Decode a data URL into an off-screen canvas (for re-scanning the QR). */
async function dataUrlToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(img, 0, 0);
  return canvas;
}
